/**
 * Utilitaires généraux pour BTPal
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
// Type seul : effacé à la compilation, donc pas de cycle à l'exécution.
import type { BadgeVariant } from '@/components/ui/badge';

// ========================================
// Classe CSS utilities
// ========================================

/**
 * Merge Tailwind classes intelligemment
 */
export function cn(...inputs: ClassValue[]): string {
    return twMerge(clsx(inputs));
}

/**
 * Comportement de défilement respectant `prefers-reduced-motion` : `scrollIntoView`
 * en JS n'est pas couvert par la neutralisation CSS globale des animations.
 */
export function scrollBehavior(): ScrollBehavior {
    if (typeof window === 'undefined' || !window.matchMedia) return 'smooth';
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

// ========================================
// Formatage
// ========================================

/**
 * Formate un montant en euros. Toujours 2 décimales : convention des logiciels
 * comptables, et sans elle "2 350,5 €" laissait croire à un montant tronqué.
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Formate un pourcentage à la française. `Math.round(x * 10) / 10` sortait
 * "12.3%" avec le point décimal anglais au milieu d'un écran en français.
 */
export function formatPercent(value: number, maximumFractionDigits = 1): string {
    return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits }).format(value)} %`;
}

/**
 * Lit un montant saisi à la française : espaces (y compris insécables) comme
 * séparateurs de milliers, virgule décimale, symbole € toléré.
 * `parseFloat` seul lit "15 000 €" comme 15.
 */
export function parseAmount(value: string): number | undefined {
    const cleaned = value.replace(/[\s€]/g, '').replace(',', '.');
    if (!cleaned) return undefined;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Formate une date en français
 */
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(date);
}

/**
 * Formate une date courte (JJ/MM/AAAA)
 */
export function formatDateShort(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

/**
 * Formate un horodatage (JJ/MM/AAAA à HH:MM). L'heure compte quand la date sert
 * de preuve, comme l'acceptation d'un devis par le client.
 */
export function formatDateTimeShort(dateString: string): string {
    const date = new Date(dateString);
    return `${formatDateShort(dateString)} à ${new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)}`;
}

/**
 * Formate une date relative (dans X jours, il y a X jours)
 */
export function formatRelativeDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Demain';
    if (diffDays === -1) return 'Hier';
    if (diffDays > 0) return `Dans ${diffDays} jours`;
    return `Il y a ${Math.abs(diffDays)} jours`;
}

/**
 * Convertit une date en format ISO pour les inputs
 */
export function toInputDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split('T')[0];
}

// ========================================
// Validation
// ========================================

/**
 * Vérifie si un email est valide
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Vérifie si un SIRET est valide (14 chiffres)
 */
export function isValidSiret(siret: string): boolean {
    const cleanSiret = siret.replace(/\s/g, '');
    return /^\d{14}$/.test(cleanSiret);
}

// ========================================
// Labels / Traductions
// ========================================

export const invoiceStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    EMITTED: 'Émise',
    PAID: 'Payée',
    OVERDUE: 'En retard',
    CANCELLED: 'Annulée',
};

export const expenseCategoryLabels: Record<string, string> = {
    SALARIES: 'Salaires',
    MATERIALS: 'Matériaux',
    RENT: 'Loyer',
    INSURANCE: 'Assurance',
    TOOLS: 'Outillage',
    SUBCONTRACTING: 'Sous-traitance',
    TRANSPORT: 'Transport',
    FUEL: 'Carburant',
    VEHICLE_RENTAL: 'Location véhicule',
    TRAINING: 'Formation',
    TAXES: 'Taxes / cotisations',
    OTHER: 'Autre',
};

export const expenseFrequencyLabels: Record<string, string> = {
    MONTHLY: 'Mensuelle',
    QUARTERLY: 'Trimestrielle',
    YEARLY: 'Annuelle',
};

export const expenseStatusLabels: Record<string, string> = {
    PLANNED: 'Planifiée',
    COMMITTED: 'Engagée',
    PAID: 'Payée',
    CANCELLED: 'Annulée',
};

export const quoteStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    SENT: 'Finalisé',
    ACCEPTED: 'Accepté',
    REFUSED: 'Refusé',
    EXPIRED: 'Expiré',
};

export const projectStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    CONFIRMED: 'Confirmé',
    IN_PROGRESS: 'En cours',
    COMPLETED: 'Terminé',
    SETTLED: 'Soldé',
    CANCELLED: 'Annulé',
};

export const activityLabels: Record<string, string> = {
    electricien: 'Électricien',
    plombier: 'Plombier',
    macon: 'Maçon',
    couvreur: 'Couvreur',
    carreleur: 'Carreleur',
    menuisier: 'Menuisier',
    peintre: 'Peintre',
    chauffagiste: 'Chauffagiste',
    autre: 'Autre',
};

// ========================================
// Couleurs des statuts
// ========================================

export function getQuoteStatusVariant(status: string): BadgeVariant {
    const tones: Record<string, BadgeVariant> = {
        DRAFT: 'neutral',
        SENT: 'info',
        ACCEPTED: 'success',
        REFUSED: 'danger',
        EXPIRED: 'warning',
    };
    return tones[status] ?? 'neutral';
}

export function getProjectStatusVariant(status: string): BadgeVariant {
    const tones: Record<string, BadgeVariant> = {
        DRAFT: 'neutral',
        CONFIRMED: 'info',
        IN_PROGRESS: 'warning',
        COMPLETED: 'success',
        SETTLED: 'brand',
        CANCELLED: 'neutral',
    };
    return tones[status] ?? 'neutral';
}

export function getExpenseStatusVariant(status: string): BadgeVariant {
    const tones: Record<string, BadgeVariant> = {
        PLANNED: 'info',
        COMMITTED: 'warning',
        PAID: 'success',
        CANCELLED: 'neutral',
    };
    return tones[status] ?? 'neutral';
}

export function getInvoiceStatusVariant(status: string): BadgeVariant {
    const tones: Record<string, BadgeVariant> = {
        DRAFT: 'draft',
        EMITTED: 'emitted',
        PAID: 'paid',
        OVERDUE: 'overdue',
        CANCELLED: 'cancelled',
    };
    return tones[status] ?? 'neutral';
}
